import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon, ClockIcon, CalendarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogPosts, Category } from '../data/blogPosts';
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-957">
      {children}
    </motion.div>;
}
const categories: Category[] = ['Todos', 'Para Agentes', 'Para Agencias', 'Herramientas', 'Estrategia'];
const categoryColors: Record<Category, string> = {
  Todos: 'bg-accent/10 text-accent border-accent/20',
  'Para Agentes': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Para Agencias': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  Herramientas: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  Estrategia: 'bg-accent/10 text-accent border-accent/20'
};
export function RecursosPage() {
  const [activeCategory, setActiveCategory] = useState<Category>('Todos');
  const filtered = activeCategory === 'Todos' ? blogPosts : blogPosts.filter(a => a.category === activeCategory);
  return <div className="bg-charcoal" data-id="element-958">
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-959">
        <div className="max-w-4xl mx-auto text-center" data-id="element-960">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-961">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold tracking-wide uppercase mb-6" data-id="element-962">
              Recursos
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-963">
            Recursos prácticos para{' '}
            <span className="text-accent" data-id="element-964">agentes y agencias</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-[#b8b4ae] max-w-xl mx-auto" data-id="element-965">
            Guías, frameworks y herramientas para construir tu sistema.
          </motion.p>
        </div>
      </section>

      {/* RECURSOS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border-subtle" data-id="element-966">
        <div className="max-w-6xl mx-auto" data-id="element-967">
          {/* Filter Tabs */}
          <FadeInSection data-id="element-968">
            <div className="flex flex-wrap gap-2 mb-10" data-id="element-969">
              {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${activeCategory === cat ? 'bg-accent text-charcoal border-accent' : 'bg-surface border-border-subtle text-[#b8b4ae] hover:text-cream hover:border-accent/30'}`} data-id="element-970">
                  {cat}
                </button>)}
            </div>
          </FadeInSection>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-971">
            {filtered.map((article, i) => <FadeInSection key={article.slug} delay={i * 0.07} data-id="element-972">
                <Link to={`/recursos/${article.slug}`} className="bg-surface border border-border-subtle rounded-2xl p-6 flex flex-col h-full group hover:border-accent/40 transition-colors duration-300" data-id="element-973">
                  <div className="mb-4 flex items-center justify-between" data-id="element-974">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${categoryColors[article.category]}`} data-id="element-975">
                      {article.category}
                    </span>
                  </div>
                  <div className="flex-1" data-id="element-976">
                    <h3 className="font-semibold text-cream text-lg leading-snug mb-3 group-hover:text-accent transition-colors duration-200" data-id="element-977">
                      {article.title}
                    </h3>
                    <p className="text-sm text-[#b8b4ae] leading-relaxed mb-5" data-id="element-978">
                      {article.description}
                    </p>
                  </div>
                  <div className="mt-auto pt-4 border-t border-border-subtle flex items-center justify-between" data-id="element-979">
                    <div className="flex items-center gap-3 text-xs text-[#7a7672]" data-id="element-980">
                      <span className="flex items-center gap-1" data-id="element-981">
                        <CalendarIcon size={12} data-id="element-982" />
                        {article.publishDate}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-983">
                        <ClockIcon size={12} data-id="element-984" />
                        {article.readTime}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs text-accent font-semibold group-hover:gap-2.5 transition-all duration-200" data-id="element-985">
                      Leer más
                      <ArrowRightIcon size={12} data-id="element-986" />
                    </span>
                  </div>
                </Link>
              </FadeInSection>)}
          </div>

          {/* Coming Soon */}
          <FadeInSection delay={0.2} data-id="element-987">
            <div className="mt-10 text-center py-8 border border-dashed border-border-subtle rounded-2xl" data-id="element-988">
              <p className="text-[#7a7672] text-sm" data-id="element-989">
                Próximamente más recursos — guías, plantillas y casos de
                estudio.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-990">
        <div className="max-w-3xl mx-auto text-center" data-id="element-991">
          <FadeInSection data-id="element-992">
            <div className="bg-elevated border border-accent/20 rounded-3xl p-10 sm:p-14" data-id="element-993">
              <span className="inline-block w-12 h-1 bg-accent rounded-full mb-6" data-id="element-994" />
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-4" data-id="element-995">
                ¿Quieres recursos personalizados para tu caso?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed" data-id="element-996">
                Agenda un diagnóstico y te enviamos los recursos específicos
                para tu situación.
              </p>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200 text-base" data-id="element-997">
                Agendar diagnóstico
                <ArrowRightIcon size={18} data-id="element-998" />
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}